import type { PortalCustomization } from "@redotech/customer-portal-sdk/rpc/schema/portal-customization/get-portal-customization";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { ReturnContext } from "../../contexts/return";
import { RpcClientContext } from "../../contexts/rpc";
import { resolveShowAvailableStoreCredit } from "../../customization/compensation-method";

const BALANCE_STALE_MS = 1000 * 60 * 5;

/**
 * Display-only bonus. Applied on the draft return invoice, never in cart math.
 * Shared key so the navbar and every screen resolve to one request.
 */
export function useStoreCreditBalance({
  customization,
}: {
  customization?: PortalCustomization | null;
}): number {
  const { draftReturn } = useContext(ReturnContext);
  const client = useContext(RpcClientContext);
  const enabled = resolveShowAvailableStoreCredit(customization);

  const balanceLoad = useQuery({
    queryKey: ["storeCreditBalance", draftReturn?._id ?? null],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const result = await client!.getCustomerStoreCreditBalance({
        draftReturnId: draftReturn?._id,
      });
      return result.balance;
    },
    enabled: !!client && enabled,
    staleTime: BALANCE_STALE_MS,
    /** The handler already fails soft to zero, so a retry only repeats the cost. */
    retry: false,
  });

  return balanceLoad.data ?? 0;
}
